import { useEffect, useRef, useState, type MutableRefObject } from 'react';

import invariant from 'tiny-invariant';

import {
	dropTargetForElements,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';

import { type IsItemData, type ItemData } from '../dnd-data';

type useDragStateProps<T = unknown> = {
	dragAndDropEnabled?: boolean;
	isItemData: IsItemData<ItemData>;
};
export function useDragState({ dragAndDropEnabled = false, isItemData }: useDragStateProps): {
	groupDropIndicatorRef: MutableRefObject<HTMLDivElement | null>;
	// eslint-disable-next-line perfectionist/sort-object-types
	dragState: 'idle' | 'is-over';
	// eslint-disable-next-line perfectionist/sort-object-types
	dragSource: string | null;
} {
	const groupDropIndicatorRef = useRef<HTMLDivElement | null>(null);
	const [dragState, setDragState] = useState<'idle' | 'is-over'>('idle');
	const [dragSource, setDragSource] = useState<string | null>(null);

	// Track drag source menuItemId
	useEffect(() => {
		if (!dragAndDropEnabled) {
			return;
		}

		return monitorForElements({
			canMonitor: ({ source }) => isItemData(source.data),
			onDragStart({ source }) {
				setDragSource(source.data.menuItemId as string);
			},
			onDrop() {
				setDragSource(null);
			},
		});
	}, [isItemData, dragAndDropEnabled]);

	// Track drop target source
	useEffect(() => {
		if (!dragAndDropEnabled) {
			return;
		}

		const groupDropIndicator = groupDropIndicatorRef.current;
		invariant(groupDropIndicator, 'groupDropIndicatorRef should not be null');
		return dropTargetForElements({
			element: groupDropIndicator,
			canDrop: ({ source }) => isItemData(source.data),
			onDragStart() {
				setDragState('is-over');
			},
			onDragEnter() {
				setDragState('is-over');
			},
			onDragLeave() {
				setDragState('idle');
			},
			onDrop() {
				setDragState('idle');
			},
		});
	}, [dragAndDropEnabled, isItemData]);

	return {
		groupDropIndicatorRef,
		dragState,
		dragSource,
	};
}
